/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {Environment, getEnvironment} from '@ion/core/src/environment/environment';
import {hasRole} from '@ion/core/src/security/helpers';
import {User} from '@ion/core/src/types/entities';

type EnvironmentOverride = keyof typeof Environment;

/**
 * Defines the Role IDs for the CalAmp Support Role in each environment.
 */
const CalAmpSupportRoleIds = {
  [Environment.dev] : [43139],
  [Environment.qa]  : [17072],
  [Environment.stg] : [8933],
  [Environment.us]  : [3536],
  [Environment.eu]  : [5711]
};

/**
 * Defines the Role IDs for the `Internal Vehicle Engineering` Role in each environment.
 */
const CalampInternalVESRoleIds = {
  [Environment.dev] : [110598],
  [Environment.qa]  : [116340],
  [Environment.stg] : [11434],
  [Environment.us]  : [10237],
  [Environment.eu]  : [8649]
};

/**
 * Resolves the environment used for role checks, allowing an override through
 * the `environment` query parameter.
 */
const resolveRoleEnvironment = (): Environment => {
  const environment = getEnvironment();
  let override: string | null = null;
  if (globalThis?.location?.search) {
    override = new URLSearchParams(globalThis.location.search).get('environment');
  }
  if (override && Environment[override as EnvironmentOverride]) {
    return Environment[override as EnvironmentOverride];
  }
  return environment;
};

/**
 * Checks if the user has the CalAmp Support Role.
 */
export const hasCalAmpSupportRole = (user: User): boolean => {
  const environment = resolveRoleEnvironment();
  const roles = user.roles ?? [];
  if (environment === Environment.local) {
    return (
      hasRole(roles, CalAmpSupportRoleIds[Environment.qa]) ||
      hasRole(roles, CalAmpSupportRoleIds[Environment.dev]) ||
      hasRole(roles, CalAmpSupportRoleIds[Environment.us])
    );
  }
  return hasRole(roles, CalAmpSupportRoleIds[environment]);
};

/**
 * Checks if the user has the `Internal Vehicle Engineering` Role.
 */
export const hasInternalVehicleEngineeringRole = (user: User): boolean => {
  const environment = resolveRoleEnvironment();
  const roles = user.roles ?? [];
  if (environment === Environment.local) {
    return (
      hasRole(roles, CalampInternalVESRoleIds[Environment.qa]) ||
      hasRole(roles, CalampInternalVESRoleIds[Environment.dev]) ||
      hasRole(roles, CalampInternalVESRoleIds[Environment.us]) ||
      hasRole(roles, CalampInternalVESRoleIds[Environment.eu])
    );
  }
  return hasRole(roles, CalampInternalVESRoleIds[environment]);
};

/**
 * Checks if the user can access Force Validation Mode.
 * Access is restricted to Internal Vehicle Engineering and CalAmp Support roles.
 */
export const hasForceValidationAccess = (user: User): boolean => {
  return hasInternalVehicleEngineeringRole(user) || hasCalAmpSupportRole(user);
};

/**
 * Checks if the user can access the VEScout Audit Log.
 * Access is restricted to Internal Vehicle Engineering and CalAmp Support roles.
 */
export const hasVeScoutAuditAccess = (user: User): boolean => {
  return hasInternalVehicleEngineeringRole(user) || hasCalAmpSupportRole(user);
};
